<script>
  import { get } from 'vuex-pathify'
  import VueRouter from 'vue-router'
  import router from '@/router'
  import { events } from '@/helpers/gtm'
  import screen from '@/helpers/screen'

  export default {
    data() {
      return {
        isBrandsExpanded: false,
        mobileExpanded: false,
        categoryExpanded: {},
      }
    },
    computed: {
      ...get('marketplace', [
        'showFavoritesOnly',
        'showInStockOnly',
        'filters',
        'getFiltersCount',
        'availableCategories',
        'brands',
      ]),
      ...get('products', ['loaded']),
      ...get('config', ['popUpMarketEnabled']),
      screen,
      visibleBrands() {
        if (this.isBrandsExpanded) {
          return this.brands
        }
        return this.brands.slice(0, 20)
      },
    },
    watch: {
      availableCategories: {
        immediate: true,
        handler(newValue, oldValue) {
          // When the categories load, expand any parent category that contains checked children
          if (!oldValue && newValue) {
            const categoriesToExpand = this.availableCategories.filter((category) =>
              category.children?.some((childCategory) => this.isCategoryChecked(childCategory))
            )
            categoriesToExpand.forEach((category) => {
              this.setCategoryExpanded(category, true)
            })
          }
        },
      },
    },
    methods: {
      setShowFavoritesOnly(showFavoritesOnly) {
        this.$store.set('marketplace/showFavoritesOnly', showFavoritesOnly)
      },
      setShowInStockOnly(showInStockOnly) {
        this.$store.set('marketplace/showInStockOnly', showInStockOnly)
      },
      isFilterChecked(name, value) {
        return this.filters[name].includes(String(value))
      },
      async updateRoute() {
        try {
          await router.replace(this.$store.get('marketplace/pathWithFilters'))
        } catch (error) {
          // Don’t throw an error if the navigation is duplicated
          if (VueRouter.isNavigationFailure(error, VueRouter.NavigationFailureType.duplicated)) {
            return
          }
          return Promise.reject(error)
        }
      },
      setFilterChecked(name, value, checked) {
        const updatedFilterValues = this.filters[name].filter(
          (filterValue) => filterValue !== String(value)
        )
        if (checked) {
          updatedFilterValues.push(String(value))
        }
        this.$store.set('marketplace/filters', {
          ...this.filters,
          [name]: updatedFilterValues,
        })
        this.updateRoute()

        if (checked) {
          events.log({
            name: 'checked filter',
            data: {
              filter: value,
              filterType: name,
              filterCount: this.getFiltersCount,
            },
          })
        }
      },
      isCategoryChecked(category) {
        return this.isFilterChecked('categories', category.slug)
      },
      setCategoryChecked(category, checked, parent = null) {
        this.setFilterChecked('categories', category.slug, checked)
        // checking a child will uncheck parent
        if (checked && parent) {
          this.setFilterChecked('categories', parent.slug, false)
        }
      },
      isCategoryExpanded(category) {
        return this.categoryExpanded[category.slug] ?? false
      },
      setCategoryExpanded(category, expanded) {
        this.$set(this.categoryExpanded, category.slug, expanded)
      },
      isBrandChecked(index) {
        return this.isFilterChecked('brands', index)
      },
      setBrandChecked(index, checked) {
        this.setFilterChecked('brands', index, checked)
      },
      clearFilters() {
        this.$store.set('marketplace/filters', {})
        this.toggleMobileFilters()
        this.updateRoute()
      },
      toggleMobileFilters() {
        this.mobileExpanded = !this.mobileExpanded
        this.$emit('mobileExpanded')
      },
    },
  }
</script>

<template>
  <div :class="`static ${popUpMarketEnabled ? 'bg-dawn' : ''}`">
    <UtilityConditionalWrapper :wrap="popUpMarketEnabled" tag="BaseContainer" max-width="2xl">
      <div
        :class="`sticky top-0 z-20 flex items-center bg-dawn ${
          popUpMarketEnabled
            ? 'justify-between py-3'
            : 'lg:static lg:flex-col lg:items-stretch lg:bg-transparent py-3 px-4 -mx-6 sm:-mx-9 lg:mx-0 lg:p-0'
        }`"
      >
        <div v-if="popUpMarketEnabled" class="font-semibold"> Shop Bundles </div>
        <div v-if="!popUpMarketEnabled" class="static flex flex-grow lg:mb-8">
          <!-- Desktop: "Filters" heading (always visible) -->
          <h2 class="hidden lg:block font-semibold text-base">Filters</h2>
          <!-- Mobile: "Filters" toggle button -->
          <button type="button" class="static lg:hidden rounded-sm">
            <!-- Allow to click anywhere in the "Filters" section -->
            <span class="absolute inset-x-0 top-0 h-19" />
            <span class="flex items-center" data-cy="filters-toggle" @click="toggleMobileFilters">
              <h2 class="font-semibold">
                Filters {{ getFiltersCount ? `(${getFiltersCount})` : '' }}
              </h2>
              <BaseIcon
                :size="5"
                :class="`ml-2 flex-shrink-0 transform ${
                  mobileExpanded ? '-rotate-180' : ''
                } transition duration-200`"
              >
                <IconChevronDown />
              </BaseIcon>
            </span>
          </button>
        </div>

        <!-- COMMENTED OUT: old Favorites toggle (BaseSwitch) — replaced by checkbox -->
        <!--
        <span class="flex items-center">
          <BaseIcon :size="3" class="mr-1 stroke-current text-summer">
            <IconHeart />
          </BaseIcon>
          <span class="font-semibold">Favorites</span>
          <BaseSwitch
            data-cy="favorites-toggle"
            :checked="showFavoritesOnly"
            label-position="left"
            @change="setShowFavoritesOnly($event)"
          >
          </BaseSwitch>
        </span>
        -->
        <!-- Favorites and In Stock checkboxes -->
        <span class="flex flex-col gap-2">
          <BaseCheckbox
            data-cy="favorites-toggle"
            :checked="showFavoritesOnly"
            size="sm"
            vertical-alignment="center"
            @change="setShowFavoritesOnly($event)"
          >
            <span class="flex items-center">
              <BaseIcon :size="3" class="mr-1 stroke-current text-summer">
                <IconHeart />
              </BaseIcon>
              <span class="font-semibold">Favorites</span>
            </span>
          </BaseCheckbox>
          <!-- Desktop only: In Stock lives here; on mobile it moves into the slide-out -->
          <BaseCheckbox
            data-cy="in-stock-toggle"
            class="hidden lg:flex"
            :checked="showInStockOnly"
            size="sm"
            vertical-alignment="center"
            @change="setShowInStockOnly($event)"
          >
            <span class="font-semibold">In Stock</span>
          </BaseCheckbox>
        </span>
      </div>
    </UtilityConditionalWrapper>

    <!-- Category pills moved to MarketCategoryPills.vue, rendered above the catalog grid in MarketCatalog.vue -->

    <component
      :is="screen.lg ? 'div' : 'BaseSlideUpDown'"
      v-if="!popUpMarketEnabled"
      :expanded="mobileExpanded"
    >
      <!-- Skeleton filter rows while products/tags/categories are loading -->
      <div v-if="!loaded" class="mt-8 space-y-3">
        <div v-for="i in 9" :key="i" class="flex items-center gap-2">
          <div class="skeleton w-4 h-4 rounded flex-shrink-0" />
          <div
            class="skeleton h-3 rounded"
            :style="`width: ${[70, 90, 60, 80, 75, 65, 85, 55, 72][i - 1]}%`"
          />
        </div>
      </div>

      <div v-if="loaded">
        <!-- Mobile only: In Stock at the top of the slide-out -->
        <div class="lg:hidden mt-6">
          <BaseCheckbox
            data-cy="in-stock-toggle-mobile"
            :checked="showInStockOnly"
            size="sm"
            vertical-alignment="center"
            @change="setShowInStockOnly($event)"
          >
            <span class="font-semibold">In Stock</span>
          </BaseCheckbox>
        </div>

        <BaseAccordion :expanded="true" button-inner-classes="py-1" class="mt-8">
          <template v-slot:buttonLabel>Categories</template>
          <template v-slot>
            <UtilityRender
              tag="ul"
              class="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-1 gap-10px py-3"
              children-wrapper-tag="li"
            >
              <BaseAccordion
                v-for="category in availableCategories"
                :key="category.slug"
                :expanded="isCategoryExpanded(category)"
                @toggle="setCategoryExpanded(category, $event)"
              >
                <template v-slot:button="{ expanded, aria, toggle }">
                  <div class="flex justify-between">
                    <BaseCheckbox
                      data-cy="categories"
                      :checked="isCategoryChecked(category)"
                      size="sm"
                      class="z-10"
                      @change="
                        setCategoryChecked(category, $event)
                        if ($event && !expanded) toggle()
                      "
                    >
                      {{ category.title }}
                    </BaseCheckbox>
                    <button
                      v-if="category.children.length"
                      type="button"
                      class="absolute left-0 top-0 flex justify-end items-center w-full h-full rounded-sm"
                      v-bind="aria"
                      @click="toggle"
                    >
                      <BaseIcon
                        :label="expanded ? 'Collapse' : 'Expand'"
                        :size="4"
                        :class="`mr-1 mt-2px transform ${
                          expanded ? '-rotate-180' : ''
                        } transition duration-200`"
                      >
                        <IconChevronDown />
                      </BaseIcon>
                    </button>
                  </div>
                </template>
                <template v-if="category.children.length" v-slot>
                  <UtilityRender
                    tag="ul"
                    class="grid grid-cols-1 gap-10px pl-6 py-3"
                    children-wrapper-tag="li"
                    children-wrapper-classes="flex"
                  >
                    <BaseCheckbox
                      v-for="childCategory in category.children"
                      :key="childCategory.slug"
                      :checked="isCategoryChecked(childCategory)"
                      size="sm"
                      @change="setCategoryChecked(childCategory, $event, category)"
                    >
                      {{ childCategory.title }}
                    </BaseCheckbox>
                  </UtilityRender>
                </template>
              </BaseAccordion>
            </UtilityRender>
          </template>
        </BaseAccordion>

        <!-- COMMENTED OUT: Brands section — not in Figma redesign -->
        <!--
        <BaseAccordion :expanded="true" button-inner-classes="py-1" class="mt-6">
          <template v-slot:buttonLabel>Brands</template>
          <template v-slot>
            <UtilityRender
              tag="ul"
              class="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-1 gap-10px py-3"
              children-wrapper-tag="li"
              children-wrapper-classes="flex"
            >
              <BaseCheckbox
                v-for="(brand, index) in visibleBrands"
                :key="index"
                :checked="isBrandChecked(index)"
                size="sm"
                @change="setBrandChecked(index, $event)"
              >
                {{ brand }}
              </BaseCheckbox>
            </UtilityRender>
            <div class="mt-2 text-2xs">
              <BaseLinkStyled color="black" @click="isBrandsExpanded = !isBrandsExpanded">
                {{ isBrandsExpanded ? 'Show less' : 'Show more' }}
              </BaseLinkStyled>
            </div>
          </template>
        </BaseAccordion>
        -->

        <div class="mt-10 lg:hidden">
          <BaseButton @click="toggleMobileFilters">Apply</BaseButton>
          <div class="mt-4 flex justify-center text-sm">
            <BaseLinkStyled
              color="black"
              data-cy="market-catalog-filters-clear"
              @click="clearFilters"
              >Clear</BaseLinkStyled
            >
          </div>
        </div>
      </div>
    </component>
  </div>
</template>

<style lang="postcss" scoped>
  @keyframes skeleton-shimmer {
    0% {
      background-position: -400px 0;
    }

    100% {
      background-position: 400px 0;
    }
  }

  .skeleton {
    display: block;
    background: linear-gradient(90deg, #ede5d8 25%, #f5ede0 50%, #ede5d8 75%);
    background-size: 800px 100%;
    animation: skeleton-shimmer 1.5s infinite linear;
  }

  .space-y-3 > * + * {
    margin-top: 0.75rem;
  }
</style>
